/**
 * Générateur — Automatisme : simplifier des expressions littérales.
 * Attendu ID : 1227 | Niveau : 3ème | Domaine : Calcul littéral et algébrique
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 11, ligne 795).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * EXTENSION de gen_1109 (4e : 3x + 2x, 3x × 2x, x + x, x × x, coefficients
 * 2..9) : ici RÉDUIRE une somme de trois ou quatre termes MÊLÉS (des x, des
 * nombres, parfois des x²) avec des coefficients RELATIFS (−9..9), le neuf
 * de 3e (mise en œuvre Eduscol 2026 p. 18 « réduire −x − (5x − 2) ;
 * 2x² − 3x + x² »). Résultat à DEUX termes non nuls. Frontières : les
 * parenthèses (1233, l. 800), les facteurs communs (objectif 3e l. 803),
 * x³ et x × x², les produits signés (choix mode auto : sommes seulement, À
 * ARBITRER), le coefficient ±1 dans les trous (au QCM et au Vrai/Faux
 * seulement, À ARBITRER).
 * Banque MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 4NE2
 * (4 à 6 termes, coefficients −9..9), 4NE3 (avec x²), 5NE1/5NE6 ; retenu :
 * 3 ou 4 termes, x² une série sur trois.
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Réduis » (reduire_saisie) : « Réduis 3x + 5 − x + 2 » en
 *     SAISIE LIBRE, comparaison d'expression (décision prof 13/09) : « 2x + 7 »,
 *     « 7 + 2x », « +2x + 7 », « 2*x + 7 » admis ; un carré une fois sur trois,
 *     le corrigé écrit « x² » (touche ² du clavier), « x^2 » tapé est admis.
 *   Carte 2 « Réduire » (reduire_qcm_unique, nom de 1109) : « 2n² + 4n + 3n²
 *     + 5, réduit, c'est… », QUATRE choix : la clé, termes_non_semblables
 *     (9n² + 5), tout_fusionne (14n²), carre_de_trop (5n⁴ + 4n + 5) ou
 *     signe_perdu / coefficients_multiplies (l'item DEPP 2de 2025 q.10 tel
 *     quel quand il sort).
 *   Carte 3 « Vrai ou faux » (sens_tableau_vf) : quatre égalités
 *     « expression = réduite » vérifiées comme POLYNÔMES (coefficient par
 *     coefficient).
 *
 * INVARIANTS : chaque réduction re-calculée en regroupant par degré (throw si
 * la ligne affichée ne réduit pas à la clé) ; les distracteurs distincts de
 * la clé et entre eux comme polynômes ; jamais un terme nul affiché.
 * SACRIFIÉ (fiche « Les x ensemble, les nombres ensemble ») : les trois
 * termes {3x, 4, 2x} dans tous les ordres, plus (3 ; 2) importé de 1109 ;
 * auto-testé par gen1227.self.test.js.
 *
 * ── Erreurs (familles nommées ; DEPP 2de 2025 q.10 « 2n² + 4n + 3n² + 5 »,
 * réussite 85,1 %) ────────────────────────────────────────────────────────
 *   termes_non_semblables    : 9n² + 5 (les n² et les n fusionnés), PRINCIPALE
 *                              (8,2 %) ;
 *   tout_fusionne            : 14n² (3,6 %) ;
 *   carre_de_trop            : 5n⁴ + 4n + 5 (les exposants ajoutés) ;
 *   signe_perdu              : 3x − 5x → 2x ;
 *   coefficients_multiplies  : 3x + 2x → 6x.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie, tex, ecrituresPolynome,
} from '../../../utils';
import { COUPLE_SACRIFIE as SACRIFIE_1109 } from '../../4eme/calcul-litteral/gen_1109';

const ATTENDU_ID = 1227;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_SAISIE = 'Réduis';
const L_QCM = 'Réduire';
const L_VF = 'Vrai ou faux';

const LETTRES = ['x', 'x', 'x', 'n', 't', 'y'];

// ─── Le domaine : des listes de termes { coef, deg } ──────────────────────

/** Les termes de la fiche : 3x + 4 + 2x, sacrifiés dans tous les ordres ; (3 ; 2) importé de 1109. */
export const SACRIFIE = [{ coef: 3, deg: 1 }, { coef: 4, deg: 0 }, { coef: 2, deg: 1 }];
export { SACRIFIE_1109 };
const signature = (termes) => termes.map((t) => `${t.coef}d${t.deg}`).sort().join(',');
const estSacrifie = (termes) => signature(termes) === signature(SACRIFIE)
    || (termes.length === 2 && termes.every((t) => t.deg === 1) && signature(termes) === signature([{ coef: SACRIFIE_1109.a, deg: 1 }, { coef: SACRIFIE_1109.b, deg: 1 }]));

/** Regroupe par degré (assert : au moins un terme), degrés décroissants, termes nuls retirés. */
const reduire = (termes) => {
    if (!termes.length) throw new Error('gen_1227 : expression vide');
    const parDeg = new Map();
    termes.forEach((t) => parDeg.set(t.deg, (parDeg.get(t.deg) || 0) + t.coef));
    return [...parDeg.entries()].filter(([, c]) => c !== 0).sort((u, v) => v[0] - u[0]).map(([deg, coef]) => ({ coef, deg }));
};
const memePolynome = (u, v) => signature(reduire(u)) === signature(reduire(v));

/**
 * Une expression de 3 ou 4 termes mêlés : degrés 1 et 0 (et 2 si `carre`),
 * coefficients −9..9 non nuls, au moins deux degrés présents, deux termes de
 * même degré au moins, résultat à DEUX termes non nuls ; `sansUn` interdit
 * les coefficients ±1 dans le résultat (série flash) ; `constantePositive`
 * impose un second terme positif au résultat.
 */
const tirer = ({ carre = false, sansUn = false, secondPositif = false } = {}) => {
    for (let essai = 0; essai < 300; essai++) {
        const n = randInt(3, 4);
        const degres = carre ? [2, 2, 1, ...(n === 4 ? [randomChoice([1, 2])] : [])] : [1, 1, 0, ...(n === 4 ? [randomChoice([0, 1])] : [])];
        const termes = shuffle(degres).map((deg) => ({ coef: randomChoice([-9, -8, -7, -6, -5, -4, -3, -2, -1, 1, 2, 3, 4, 5, 6, 7, 8, 9]), deg }));
        if (termes[0].coef < 0 && randomChoice([true, false])) continue; // un premier terme négatif une fois sur deux au plus
        const r = reduire(termes);
        if (r.length !== 2) continue;
        if (sansUn && r.some((t) => Math.abs(t.coef) === 1)) continue;
        if (secondPositif && r[1].coef < 0) continue;
        if (estSacrifie(termes)) continue;
        // assert : la réduction re-faite terme à terme donne bien r
        r.forEach((t) => { if (termes.filter((u) => u.deg === t.deg).reduce((s, u) => s + u.coef, 0) !== t.coef) throw new Error('gen_1227 : réduction'); });
        return { termes, r };
    }
    return carre
        ? { termes: [{ coef: 2, deg: 2 }, { coef: -3, deg: 1 }, { coef: 1, deg: 2 }], r: [{ coef: 3, deg: 2 }, { coef: -3, deg: 1 }] }
        : { termes: [{ coef: 5, deg: 1 }, { coef: 3, deg: 0 }, { coef: -2, deg: 1 }, { coef: 4, deg: 0 }], r: [{ coef: 3, deg: 1 }, { coef: 7, deg: 0 }] };
};

// ─── Écritures (KaTeX) ────────────────────────────────────────────────────

const puissance = (deg, L) => (deg === 0 ? '' : deg === 1 ? L : `${L}^{${deg}}`);
/** Un monôme sans signe : « 3x », « x », « 7 », « 2x^{2} ». */
const monome = ({ coef, deg }, L) => `${Math.abs(coef) === 1 && deg > 0 ? '' : Math.abs(coef)}${puissance(deg, L)}`;
/** Une expression : « -3x + 5 - x + 2 » (le premier signe collé, les autres espacés). */
const expressionTex = (termes, L) => termes.map((t, i) => {
    const s = t.coef < 0 ? '-' : '+';
    return i === 0 ? `${t.coef < 0 ? '-' : ''}${monome(t, L)}` : ` ${s} ${monome(t, L)}`;
}).join('');

const EXPLICATIONS = {
    termes_non_semblables: 'On ne peut ajouter que des termes semblables : les x avec les x, les x² avec les x², les nombres avec les nombres.',
    tout_fusionne: 'Tout ne se regroupe pas en un seul terme : 3x et 5 ne sont pas semblables.',
    carre_de_trop: '2x² + x² = 3x² : on ajoute les coefficients, l\'exposant ne change pas (x² × x² donnerait x⁴, mais ici on ajoute).',
    signe_perdu: 'Le signe fait partie du terme : 3x − 5x = −2x.',
    coefficients_multiplies: '3x + 2x = 5x : on AJOUTE les coefficients quand on ajoute des termes semblables.',
    regle: 'Je regroupe les termes semblables : les x ensemble, les x² ensemble, les nombres ensemble ; j\'ajoute leurs coefficients avec leurs signes.',
};

// ─── Distracteurs ─────────────────────────────────────────────────────────

/** Les candidats, chacun une liste de termes (réduite), jamais égal à la clé comme polynôme, jamais deux fois le même polynôme. */
const candidats = ({ termes, r }) => {
    const c = [];
    const pousser = (code, liste) => {
        const red = liste.filter((t) => t.coef !== 0);
        if (!red.length || memePolynome(red, r) || c.some((x) => memePolynome(x.termes, red))) return;
        c.push({ code, termes: red });
    };
    // termes_non_semblables : les deux termes du résultat fusionnés dans le plus haut degré
    pousser('termes_non_semblables', [{ coef: r[0].coef + r[1].coef, deg: r[0].deg }]);
    // tout_fusionne : tous les coefficients ajoutés dans le plus haut degré (peut coïncider avec la famille précédente : filtré)
    pousser('tout_fusionne', [{ coef: termes.reduce((s, t) => s + t.coef, 0), deg: Math.max(...termes.map((t) => t.deg)) }]);
    // carre_de_trop : deux x² ajoutés donnent x⁴
    if (termes.filter((t) => t.deg === 2).length >= 2) pousser('carre_de_trop', [{ coef: r[0].coef, deg: 4 }, r[1]]);
    // signe_perdu : un terme négatif (autre que le premier) compté positif
    const iNeg = termes.findIndex((t, i) => i > 0 && t.coef < 0);
    if (iNeg > 0) pousser('signe_perdu', reduire(termes.map((t, i) => (i === iNeg ? { ...t, coef: -t.coef } : t))));
    // coefficients_multiplies : les coefficients d'un même degré multipliés
    const deg = r[0].deg;
    const memes = termes.filter((t) => t.deg === deg);
    if (memes.length >= 2) pousser('coefficients_multiplies', reduire([{ coef: memes.reduce((p, t) => p * t.coef, 1), deg }, ...termes.filter((t) => t.deg !== deg)]));
    return c;
};

// ─── Carte 1 : Réduis (saisie libre, comparaison d'expression) ───────────

/**
 * L'élève écrit l'expression réduite lui-même (décision prof 13/09) : tous
 * les ordres des termes sont admis (contenu.reponses_acceptees), les espaces
 * et le signe moins typographique sont indifférents ; bonne_reponse (le
 * corrigé) = les termes par degré décroissant. Un carré une fois sur trois.
 */
const genSaisie = () => {
    for (let essai = 0; essai < 100; essai++) {
        const L = randomChoice(LETTRES);
        const avecCarre = randInt(1, 3) === 1;
        const it = tirer({ carre: avecCarre });
        const ecritures = ecrituresPolynome(it.r, L);
        const payload = makeSaisie(ecritures[0], `<p><b>Réduis</b> ${tex(expressionTex(it.termes, L))}.</p>`, {
            ...opts(L_SAISIE), strict: true, comparaison: 'expression', reponses_acceptees: ecritures,
        });
        payload.contenu = { ...payload.contenu, sousType: avecCarre ? 'carre' : 'simple', explications: { regle: EXPLICATIONS.regle, termes_non_semblables: EXPLICATIONS.termes_non_semblables, signe_perdu: EXPLICATIONS.signe_perdu } };
        payload._meta.params = { ...it, lettre: L, avecCarre };
        return payload;
    }
    throw new Error('gen_1227 : saisie sans tirage');
};

// ─── Carte 2 : Réduire (QCM) ──────────────────────────────────────────────

const genQcm = () => {
    for (let essai = 0; essai < 100; essai++) {
        const L = randomChoice(LETTRES);
        const it = tirer({ carre: randInt(1, 3) === 1 });
        const cands = shuffle(candidats(it));
        const pris = [];
        for (const x of cands) if (!pris.some((y) => y.code === x.code)) pris.push(x);
        if (pris.length < 3) continue;
        const bon = tex(expressionTex(it.r, L));
        const choix = [bon, ...pris.slice(0, 3).map((x) => tex(expressionTex(x.termes, L)))];
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, bon, `<p><b>${tex(expressionTex(it.termes, L))}, réduit, c'est…</b></p>`, opts(L_QCM));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { ...it, lettre: L, distracteurs: pris.slice(0, 3).map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli vérifié : 5x + 3 − 2x + 4 = 3x + 7 ; 10x (termes_non_semblables), 3x − 1 (signe_perdu), −10x + 7 (coefficients_multiplies).
    const it = { termes: [{ coef: 5, deg: 1 }, { coef: 3, deg: 0 }, { coef: -2, deg: 1 }, { coef: 4, deg: 0 }], r: [{ coef: 3, deg: 1 }, { coef: 7, deg: 0 }] };
    const bon = tex('3x + 7');
    const payload = makeQcm([bon, tex('10x'), tex('3x - 1'), tex('-10x + 7')], bon, `<p><b>${tex(expressionTex(it.termes, 'x'))}, réduit, c'est…</b></p>`, opts(L_QCM));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { ...it, lettre: 'x', distracteurs: [{ code: 'termes_non_semblables' }, { code: 'signe_perdu' }, { code: 'coefficients_multiplies' }] };
    return payload;
};

// ─── Carte 3 : Vrai ou faux ───────────────────────────────────────────────

const genVf = () => {
    for (let essai = 0; essai < 100; essai++) {
        const L = randomChoice(LETTRES);
        const items = [0, 1, 2, 3].map((i) => tirer({ carre: i === 0 }));
        const nbVraies = randomChoice([1, 2, 2, 3]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(4 - nbVraies).fill(false)]);
        const lignes = items.map((it, i) => {
            if (verites[i]) return { ligne: tex(`${expressionTex(it.termes, L)} = ${expressionTex(it.r, L)}`), code: null };
            const cands = candidats(it);
            if (!cands.length) return null;
            const x = randomChoice(cands);
            return { ligne: tex(`${expressionTex(it.termes, L)} = ${expressionTex(x.termes, L)}`), code: x.code };
        });
        if (lignes.some((l) => !l) || new Set(lignes.map((l) => l.ligne)).size < 4) continue;
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items, lettre: L, verites, distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli vérifié : 2x² − 3x + x² = 3x² − 3x V ; 5x + 3 − 2x + 4 = 3x + 7 V ; 4x + 5 − x = 8x F ; 6 − 2x + 3x = x − 6 F.
    const lignes = [tex('2x^{2} - 3x + x^{2} = 3x^{2} - 3x'), tex('5x + 3 - 2x + 4 = 3x + 7'), tex('4x + 5 - x = 8x'), tex('6 - 2x + 3x = x - 6')];
    const payload = makeVraiFaux(lignes, [0, 0, 1, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items: [], lettre: 'x', verites: [true, true, false, false], distracteurs: [{ code: 'termes_non_semblables' }, { code: 'signe_perdu' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre expressions réduites distinctes comme polynômes ; la clé a deux termes non nuls',
        'coefficients −9..9 non nuls, 3 ou 4 termes mêlés, x² une série sur trois ; sommes seulement',
        'sacrifiés : {3x, 4, 2x} dans tous les ordres, (3 ; 2) de 1109',
    ],
    reduire_qcm_unique: {
        familles: {
            termes_non_semblables: 'les deux termes du résultat fusionnés (9n² + 5), la principale (DEPP 2de 2025 q.10, 8,2 %)',
            tout_fusionne: 'tous les coefficients ajoutés en un terme (14n², 3,6 %)',
            carre_de_trop: 'x² + x² → x⁴ (les exposants ajoutés)',
            signe_perdu: 'un terme négatif compté positif',
            coefficients_multiplies: 'les coefficients d\'un même degré multipliés',
        },
    },
    vf_tableau_vf: 'quatre égalités, une en x² ; vérité = égalité des polynômes ; les fausses tirent une famille',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    reduire_saisie: genSaisie,
    reduire_qcm_unique: genQcm,
    sens_tableau_vf: genVf,
};

export const CATEGORIES = [
    // id UNIQUE par carte : deux cartes 'reduire' (13/09) donnaient deux cles React
    // identiques, la carte fantome suivait le prof d'attendu en attendu (S-CARTE-ID).
    { id: 'ecrire', label: L_SAISIE, etapes: 2, formatsList: [{ key: 'reduire_saisie', label: 'Saisie' }] },
    { id: 'reduire', label: L_QCM, etapes: 2, formatsList: [{ key: 'reduire_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_VF, etapes: 1, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
